/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
    type THEME_LIST,
    setTheme,
    setDarkMode,
    toggleAutoMode,
} from '@europrocurement/l2d-domain';
import { customizerSelector } from '@europrocurement/flexy-components/redux/storeConfig/selectors';
import { type NamedTheme, themesOptions } from '@europrocurement/flexy-components/assets';
import ThemeOptionCard from './fragments/ThemeOptionCard';

export type ThemePreferencesProps = {
    hideModeOptions?: boolean;
};

const ThemePreferences: React.FunctionComponent<ThemePreferencesProps> = function ({
    hideModeOptions = false,
}) {
    const customizer = useSelector(customizerSelector, shallowEqual);
    const dispatch = useDispatch();

    // Configuration des options de mode (light/dark/auto)
    const modeOptions = !hideModeOptions
        ? [
              {
                  key: 'light',
                  label: 'Clair',
                  backgroundColor: '#ffffff',
                  primaryColor: '#2196F3',
                  secondaryColor: '#9E9E9E',
              },
              {
                  key: 'dark',
                  label: 'Sombre',
                  backgroundColor: '#1a1a1a',
                  primaryColor: '#2196F3',
                  secondaryColor: '#9E9E9E',
              },
              {
                  key: 'auto',
                  label: 'Auto',
                  backgroundColor: '#ffffff',
                  primaryColor: '#2196F3',
                  secondaryColor: '#9E9E9E',
                  isAutoMode: true,
              },
          ]
        : [];

    // Configuration des options de couleur
    const colorOptions = themesOptions.map((theme: NamedTheme) => {
        const primaryColor = (theme.palette?.primary as any)?.main || '#2196F3';
        const secondaryColor = (theme.palette?.secondary as any)?.main || '#9E9E9E';

        return {
            key: theme.name,
            label: theme.name,
            backgroundColor: '#ffffff',
            primaryColor,
            secondaryColor,
        };
    });

    const isAutoModeEnabled = customizer.isAutoModeEnabled || false;
    const currentTheme = customizer.activeMode;

    return (
        <Box>
            <Box p={2}>
                <Typography
                    component="h2"
                    variant="l2dh6"
                    sx={{
                        lineHeight: 1.4,
                    }}
                >
                    Préférences de thème
                </Typography>
                <Typography
                    variant="l2dlabel"
                    color="text.secondary"
                >
                    Choisissez l&apos;apparence de l&apos;interface selon vos préférences
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 2,
                }}
                p={2}
            >
                {/* Options de thème (light/dark/auto) */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        '& > *': {
                            flex: {
                                xs: '1 1 calc(50% - 8px)', // 2 colonnes sur mobile
                                sm: '1 1 calc(33.333% - 11px)', // 3 colonnes sur tablet+
                            },
                            minWidth: '100px',
                            maxWidth: '140px',
                        },
                    }}
                >
                    {modeOptions.map((option) => {
                        let isSelected = false;
                        let isDisabled = false;
                        let hasSpecialEffect = false;

                        if (option.key === 'auto') {
                            hasSpecialEffect = isAutoModeEnabled;
                            isSelected = isAutoModeEnabled;
                        } else {
                            isDisabled = isAutoModeEnabled;
                            isSelected = !isAutoModeEnabled && option.key === currentTheme;
                        }

                        return (
                            <ThemeOptionCard
                                key={option.key}
                                backgroundColor={option.backgroundColor}
                                primaryColor={option.primaryColor}
                                secondaryColor={option.secondaryColor}
                                label={option.label}
                                isAutoMode={option.isAutoMode}
                                isSelected={isSelected}
                                isDisabled={isDisabled}
                                hasSpecialEffect={hasSpecialEffect}
                                onClick={() => {
                                    if (option.key === 'auto') {
                                        dispatch(toggleAutoMode(!isAutoModeEnabled));
                                        if (!isAutoModeEnabled) {
                                            const prefersDark = window.matchMedia(
                                                '(prefers-color-scheme: dark)',
                                            ).matches;
                                            dispatch(setDarkMode(prefersDark ? 'dark' : 'light'));
                                        }
                                    } else if (!isAutoModeEnabled) {
                                        dispatch(setDarkMode(option.key as 'dark' | 'light'));
                                    }
                                }}
                            />
                        );
                    })}
                </Box>

                {/* Options de couleur */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        '& > *': {
                            flex: {
                                xs: '1 1 calc(50% - 8px)', // 2 colonnes sur mobile
                                sm: '1 1 calc(33.333% - 11px)', // 3 colonnes sur tablet+
                            },
                            minWidth: '100px',
                            maxWidth: '140px',
                        },
                    }}
                >
                    {colorOptions.map((option) => {
                        const colorIsSelected = customizer.activeTheme === option.key;

                        return (
                            <ThemeOptionCard
                                key={option.key}
                                backgroundColor={option.backgroundColor}
                                primaryColor={option.primaryColor}
                                secondaryColor={option.secondaryColor}
                                label={option.label}
                                isSelected={colorIsSelected}
                                onClick={() => dispatch(setTheme(option.key as THEME_LIST))}
                            />
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default ThemePreferences;
